/**
 * epgTimeline.tsx
 */
import EpgTimelineContainer from '@components/atoms/container/epgTimelineContainer';
import EpgTimelineDate from '@components/atoms/text/epgTimelineDate';
import EpgTimelineDateContainer from '@components/atoms/container/epgTimelineDateContainer';
import EpgTimelineHoursContainer from '@components/atoms/container/epgTimelineHoursContainer';
import EpgTimeIndicator from './epgTimeIndicator';
import EpgTimelineLabel from '@components/atoms/text/epgTimelineLabel';
import EpgTimelineSlotsContainer from '@components/atoms/container/epgTimelineSlotsContainer';
import DateUtils from '@shared/utils/date';
import MainContext from '@shared/components/utils/context/mainContext';
import { memo, useContext } from 'react';

// Standard EPG slot duration; changing this also affects label density and indicator alignment.
const SLOT_DURATION_MINUTES = 30;

/**
 * Produces an array of Date objects at every 30-minute boundary between
 * `beginTime` and `endTime` (inclusive), used to render the timeline labels.
 */
const generateSlots = (beginTime: Date, endTime: Date): Date[] => {
  const slots: Date[] = [];
  const current = new Date(beginTime);
  while (current <= endTime) {
    slots.push(new Date(current));
    current.setMinutes(current.getMinutes() + SLOT_DURATION_MINUTES);
  }
  return slots;
};

/**
 * Horizontal timeline header for the EPG grid.
 *
 * Renders the broadcast date on the left, followed by a row of 30-minute slot
 * labels spanning `beginTime` → `endTime`. `EpgTimeIndicator` is mounted
 * inside the slots container so the "now" needle is positioned relative to the
 * same coordinate space as the slot labels.
 *
 * Returns nothing when `config.timeline` is not yet available (e.g. during
 * initial load before the EPG data has been fetched).
 */
function EpgTimeline() {
  const { config } = useContext(MainContext);
  if (!config.timeline) return null;
  const { start, end } = config.timeline;
  const slots = generateSlots(start, end);
  return (
    <EpgTimelineContainer>
      <EpgTimelineDateContainer>
        <EpgTimelineDate>{DateUtils.getDayTitle(start, true)}</EpgTimelineDate>
      </EpgTimelineDateContainer>
      <EpgTimelineHoursContainer>
        <EpgTimeIndicator />
        <EpgTimelineSlotsContainer>
          {slots.map((slot) => (
            <EpgTimelineLabel key={slot.getTime()}>
              {DateUtils.formatTime(slot)}
            </EpgTimelineLabel>
          ))}
        </EpgTimelineSlotsContainer>
      </EpgTimelineHoursContainer>
    </EpgTimelineContainer>
  );
}

export default memo(EpgTimeline);
